import React, { useState, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import {
  AMMUNITION_CATALOG,
  ArmorZone,
  BallisticProjectileConfig,
  ImpactResolver,
  ImpactResult,
  STANDARD_TANK_ARMOR_PROFILES,
  TankArmorProfile,
  calculateEffectiveThickness,
  calculateImpactAngle,
  calculateReflectionVector,
} from '@tankworld/armor-physics';
import { soundSystem } from '../../audio/sound-system.js';
import {
  ArrowLeft,
  Crosshair,
  Shield,
  Zap,
  Activity,
  Download,
  RotateCcw,
  Sparkles,
  Info,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileCode,
} from 'lucide-react';

interface ArmorAnalysisLabProps {
  onBack: () => void;
}

// 3D Visualizer for Armor Plate, Incoming Trajectory, Normal and Reflection
const TrajectoryVisualizer3D: React.FC<{
  impact: ImpactResult;
  zone: ArmorZone;
  angleDeg: number;
}> = ({ impact, zone, angleDeg }) => {
  const thetaRad = (angleDeg * Math.PI) / 180;

  // Impact occurs at (0, 0, 0)
  const incomingLength = 4.0;
  const inStartX = -Math.sin(thetaRad) * incomingLength;
  const inStartY = 0;
  const inStartZ = Math.cos(thetaRad) * incomingLength;

  // Reflected ray
  const isRicochet = impact.isRicochet;
  const refLength = 3.0;
  const refEndX = Math.sin(thetaRad) * refLength;
  const refEndY = 0;
  const refEndZ = Math.cos(thetaRad) * refLength;

  return (
    <group position={[0, 0, 0]}>
      {/* 1. Armor Plate 3D Mesh */}
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[3.0, 3.0, (zone.thicknessMm / 1000) * 2]} />
        <meshStandardMaterial
          color={zone.materialId === 'composite' ? '#0284c7' : '#475569'}
          metalness={0.7}
          roughness={0.3}
          transparent
          opacity={0.85}
        />
      </mesh>

      {/* 2. Surface Normal Vector (Yellow pointing outward along +Z) */}
      <mesh position={[0, 0, 1.2]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 2.4, 12]} />
        <meshBasicMaterial color="#eab308" />
      </mesh>
      <mesh position={[0, 0, 2.4]} rotation={[-Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.08, 0.2, 12]} />
        <meshBasicMaterial color="#eab308" />
      </mesh>

      {/* 3. Incoming Projectile Trajectory (Red line and shell) */}
      <mesh
        position={[inStartX / 2, 0, inStartZ / 2]}
        rotation={[0, thetaRad, 0]}
      >
        <boxGeometry args={[0.03, 0.03, incomingLength]} />
        <meshBasicMaterial color="#ef4444" />
      </mesh>
      <mesh position={[inStartX * 0.4, 0, inStartZ * 0.4]} rotation={[-Math.PI / 2, 0, thetaRad]}>
        <coneGeometry args={[0.09, 0.4, 12]} />
        <meshStandardMaterial color="#dc2626" roughness={0.3} metalness={0.8} />
      </mesh>

      {/* 4. Reflected Trajectory on Ricochet (Amber / Orange) */}
      {isRicochet && (
        <group>
          <mesh
            position={[refEndX / 2, 0, refEndZ / 2]}
            rotation={[0, -thetaRad, 0]}
          >
            <boxGeometry args={[0.03, 0.03, refLength]} />
            <meshBasicMaterial color="#f97316" />
          </mesh>
          <mesh position={[refEndX * 0.8, 0, refEndZ * 0.8]}>
            <sphereGeometry args={[0.08, 8, 8]} />
            <meshBasicMaterial color="#fb923c" />
          </mesh>
        </group>
      )}

      {/* 5. Spall Cone on Penetration (Behind the plate, -Z) */}
      {impact.isPenetrated && (
        <group position={[0, 0, -(zone.thicknessMm / 1000)]}>
          {impact.spallParticles?.map((sp, idx) => (
            <mesh
              key={idx}
              position={[sp.direction.x * 1.5, sp.direction.y * 1.5, sp.direction.z * 1.5]}
            >
              <sphereGeometry args={[0.035, 6, 6]} />
              <meshBasicMaterial color="#facc15" />
            </mesh>
          ))}
        </group>
      )}

      {/* Impact Point Flash */}
      <mesh position={[0, 0, (zone.thicknessMm / 1000) * 1.05]}>
        <sphereGeometry args={[0.09, 12, 12]} />
        <meshBasicMaterial color={impact.isPenetrated ? '#22c55e' : isRicochet ? '#f97316' : '#ef4444'} />
      </mesh>
    </group>
  );
};

export const ArmorAnalysisLab: React.FC<ArmorAnalysisLabProps> = ({ onBack }) => {
  const [selectedTankKey, setSelectedTankKey] = useState<string>('mbt_standard');
  const [selectedZoneId, setSelectedZoneId] = useState<string>('upper_glacis');
  const [selectedAmmoId, setSelectedAmmoId] = useState<string>('apfsds_125mm');
  const [angleDeg, setAngleDeg] = useState<number>(30); // 0 = normal perpendicular, 85 = grazing
  const [distanceM, setDistanceM] = useState<number>(500); // 100m to 3000m

  const currentProfile: TankArmorProfile =
    STANDARD_TANK_ARMOR_PROFILES[selectedTankKey] || STANDARD_TANK_ARMOR_PROFILES.mbt_standard;
  const currentZone: ArmorZone =
    currentProfile.zones.find((z) => z.armorId === selectedZoneId) || currentProfile.zones[0];
  const currentAmmo: BallisticProjectileConfig =
    AMMUNITION_CATALOG[selectedAmmoId] || AMMUNITION_CATALOG.apfsds_125mm;

  // Impact calculation
  const impactResult: ImpactResult = useMemo(() => {
    const resolver = new ImpactResolver();

    // Aerodynamic velocity drop over distance: v(d) = v0 * exp(-k_drag * d)
    const kDrag = (currentAmmo.dragCoefficient * currentAmmo.referenceAreaM2 * 0.5) / currentAmmo.massKg;
    const impactVelocityMs = currentAmmo.muzzleVelocityMs * Math.exp(-kDrag * distanceM);

    const thetaRad = (angleDeg * Math.PI) / 180;
    // Trajectory vector heading into the plate
    const incomingDir = {
      x: Math.sin(thetaRad),
      y: 0,
      z: -Math.cos(thetaRad),
    };
    const incomingVelocity = {
      x: incomingDir.x * impactVelocityMs,
      y: incomingDir.y * impactVelocityMs,
      z: incomingDir.z * impactVelocityMs,
    };
    // Plate surface outward normal is along +Z
    const surfaceNormal = { x: 0, y: 0, z: 1.0 };
    const impactPoint = { x: 0, y: 0, z: 0 };

    return resolver.resolveImpact(
      impactPoint,
      currentAmmo,
      incomingVelocity,
      currentZone,
      surfaceNormal
    );
  }, [currentAmmo, currentZone, angleDeg, distanceM]);

  const handleExportJson = () => {
    soundSystem.playUiClick();
    const dataStr =
      'data:text/json;charset=utf-8,' +
      encodeURIComponent(JSON.stringify(impactResult, null, 2));
    const dlAnchor = document.createElement('a');
    dlAnchor.setAttribute('href', dataStr);
    dlAnchor.setAttribute(
      'download',
      `tankworld-impact-${currentAmmo.id}-${currentZone.armorId}-${angleDeg}deg.json`
    );
    dlAnchor.click();
  };

  const getVerdictBadge = (result: string) => {
    switch (result) {
      case 'penetration':
        return (
          <span className="px-3 py-1 bg-emerald-950/80 border border-emerald-500 text-emerald-400 font-bold uppercase tracking-wider text-xs rounded flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> XUYÊN THẤU HOÀN TOÀN (PENETRATION)
          </span>
        );
      case 'ricochet':
        return (
          <span className="px-3 py-1 bg-amber-950/80 border border-amber-500 text-amber-400 font-bold uppercase tracking-wider text-xs rounded flex items-center gap-1.5">
            <RotateCcw className="w-4 h-4" /> NẢY ĐẠN HÌNH HỌC (RICOCHET)
          </span>
        );
      case 'partial_penetration':
        return (
          <span className="px-3 py-1 bg-yellow-950/80 border border-yellow-500 text-yellow-400 font-bold uppercase tracking-wider text-xs rounded flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> XUYÊN MỘT PHẦN (PARTIAL)
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-rose-950/80 border border-rose-500 text-rose-400 font-bold uppercase tracking-wider text-xs rounded flex items-center gap-1.5">
            <XCircle className="w-4 h-4" /> BỊ CHẶN LẠI (STOPPED)
          </span>
        );
    }
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col justify-between bg-zinc-950 font-mono text-zinc-100 overflow-hidden">
      {/* Top Header */}
      <div className="p-4 sm:p-6 border-b border-zinc-800 flex flex-wrap justify-between items-center gap-4 bg-zinc-950/90 z-20">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              soundSystem.playUiClick();
              onBack();
            }}
            className="p-2.5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-700 transition-all"
            title="Quay lại Menu Chính"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Crosshair className="w-6 h-6 text-emerald-500" />
              <h2 className="text-xl sm:text-2xl font-bold tracking-wider uppercase">
                PHÒNG THÍ NGHIỆM ĐẠN XUYÊN GIÁP
              </h2>
            </div>
            <p className="text-xs text-zinc-400">
              Mô phỏng quỹ đạo đạn, góc va chạm, độ dày hiệu dụng, phản xạ ricochet và năng lượng xuyên phá
            </p>
          </div>
        </div>

        {/* JSON Export Button */}
        <button
          onClick={handleExportJson}
          className="px-4 py-2 bg-zinc-900 hover:bg-emerald-600 hover:text-zinc-950 border border-emerald-700/60 text-emerald-400 font-bold text-xs uppercase tracking-wider rounded flex items-center gap-2 transition-all shadow-md"
        >
          <FileCode className="w-4 h-4" /> Xuất dữ liệu JSON
        </button>
      </div>

      {/* Main Workspace Layout */}
      <div className="flex-1 relative flex flex-col lg:flex-row overflow-hidden">
        {/* 3D Visualizer Canvas */}
        <div className="flex-1 relative bg-gradient-to-b from-zinc-900/60 via-zinc-950/80 to-zinc-950">
          <Canvas camera={{ position: [4, 2.5, 5], fov: 45 }}>
            <ambientLight intensity={0.9} />
            <directionalLight position={[8, 12, 8]} intensity={1.5} />
            <Grid
              position={[0, -1.5, 0]}
              args={[16, 16]}
              cellSize={0.5}
              cellThickness={1.0}
              cellColor="#334155"
              sectionSize={2.0}
              sectionThickness={1.5}
              sectionColor="#10b981"
              fadeDistance={20}
            />
            <TrajectoryVisualizer3D
              impact={impactResult}
              zone={currentZone}
              angleDeg={angleDeg}
            />
            <OrbitControls minDistance={2} maxDistance={10} />
          </Canvas>

          {/* 3D Legend Overlay */}
          <div className="absolute top-4 left-4 p-3 rounded-lg bg-zinc-950/85 border border-zinc-800 text-xs text-zinc-300 space-y-1.5 pointer-events-none">
            <div className="font-bold text-zinc-200 uppercase text-[11px] flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-emerald-400" /> Vector & Quỹ đạo
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-3 h-1 bg-red-500 inline-block" /> Tia đạn tới (Incoming Trajectory)
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-3 h-1 bg-yellow-400 inline-block" /> Pháp tuyến bề mặt n (Normal)
            </div>
            {impactResult.isRicochet && (
              <div className="flex items-center gap-2 text-[10px]">
                <span className="w-3 h-1 bg-orange-500 inline-block" /> Tia nảy đạn r (Ricochet Vector)
              </div>
            )}
            {impactResult.isPenetrated && (
              <div className="flex items-center gap-2 text-[10px]">
                <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" /> Nón mảnh vỡ (Spall Particles)
              </div>
            )}
          </div>
        </div>

        {/* Right Parameters & Telemetry Panel */}
        <div className="w-full lg:w-[440px] border-l border-zinc-800 bg-zinc-950/95 flex flex-col justify-between overflow-y-auto p-5 space-y-5 z-20">
          <div className="space-y-5">
            {/* Verdict Display Banner */}
            <div className="p-4 rounded-lg bg-zinc-900 border border-zinc-800 space-y-2">
              <div className="text-[10px] text-zinc-400 uppercase font-bold">Kết quả phân tích va chạm:</div>
              {getVerdictBadge(impactResult.result)}
              <div className="text-xs text-zinc-300 mt-2 leading-relaxed font-mono">
                {impactResult.solverReason}
              </div>
            </div>

            {/* Config Sliders & Selectors */}
            <div className="space-y-4">
              {/* Target Tank & Zone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-zinc-400 block mb-1">Mục tiêu thử nghiệm:</label>
                  <select
                    value={selectedTankKey}
                    onChange={(e) => {
                      soundSystem.playUiClick();
                      setSelectedTankKey(e.target.value);
                    }}
                    className="w-full p-2 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100 font-mono"
                  >
                    <option value="mbt_standard">T-72M (MBT)</option>
                    <option value="mbt_heavy">M1A1 Abrams</option>
                    <option value="light_scout">Leopard 2A4</option>
                  </select>
                </div>

                <div>
                  <label className="text-[11px] text-zinc-400 block mb-1">Vùng giáp thử nghiệm:</label>
                  <select
                    value={selectedZoneId}
                    onChange={(e) => {
                      soundSystem.playUiClick();
                      setSelectedZoneId(e.target.value);
                    }}
                    className="w-full p-2 bg-zinc-900 border border-zinc-700 rounded text-xs text-emerald-400 font-bold font-mono"
                  >
                    {currentProfile.zones.map((z) => (
                      <option key={z.armorId} value={z.armorId}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Shell Type Selector */}
              <div>
                <label className="text-[11px] text-zinc-400 block mb-1">Loại đạn pháo bắn thử:</label>
                <select
                  value={selectedAmmoId}
                  onChange={(e) => {
                    soundSystem.playUiClick();
                    setSelectedAmmoId(e.target.value);
                  }}
                  className="w-full p-2.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100 font-bold font-mono"
                >
                  {Object.values(AMMUNITION_CATALOG).map((shell) => (
                    <option key={shell.id} value={shell.id}>
                      {shell.name} ({shell.projectileType})
                    </option>
                  ))}
                </select>
              </div>

              {/* Incidence Angle Theta Slider */}
              <div className="p-3.5 rounded bg-zinc-900/60 border border-zinc-800 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-300 font-bold">Góc tới so với pháp tuyến (θ):</span>
                  <span className="text-emerald-400 font-bold">{angleDeg}°</span>
                </div>
                <div className="text-[10px] text-zinc-400">
                  Góc lệch so với phương ngang (Obliquity): <b>{90 - angleDeg}°</b>
                </div>
                <input
                  type="range"
                  min={0}
                  max={85}
                  value={angleDeg}
                  onChange={(e) => setAngleDeg(Number(e.target.value))}
                  className="w-full accent-emerald-500"
                />
              </div>

              {/* Engagement Distance Slider */}
              <div className="p-3.5 rounded bg-zinc-900/60 border border-zinc-800 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-300 font-bold">Khoảng cách tác chiến:</span>
                  <span className="text-emerald-400 font-bold">{distanceM} mét</span>
                </div>
                <div className="text-[10px] text-zinc-400">
                  Vận tốc tại điểm chạm: <b>{Math.round(impactResult.velocityBeforeMs)} m/s</b> (Đầu nòng: {currentAmmo.muzzleVelocityMs} m/s)
                </div>
                <input
                  type="range"
                  min={100}
                  max={3000}
                  step={50}
                  value={distanceM}
                  onChange={(e) => setDistanceM(Number(e.target.value))}
                  className="w-full accent-emerald-500"
                />
              </div>
            </div>

            {/* Metrics Breakdown Grid */}
            <div className="space-y-2.5 pt-2 border-t border-zinc-800">
              <div className="font-bold text-zinc-300 uppercase text-xs flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-emerald-400" /> Số liệu vi mô (Micro-Physics)
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
                <div className="p-2.5 rounded bg-zinc-900 border border-zinc-800">
                  <span className="text-zinc-400 block text-[10px]">Độ dày danh nghĩa:</span>
                  <span className="font-bold text-zinc-100">{impactResult.nominalThicknessMm} mm</span>
                </div>

                <div className="p-2.5 rounded bg-zinc-900 border border-zinc-800">
                  <span className="text-zinc-400 block text-[10px]">Độ dày đường xuyên LOS:</span>
                  <span className="font-bold text-emerald-400">{impactResult.effectiveThicknessMm} mm</span>
                </div>

                <div className="p-2.5 rounded bg-zinc-900 border border-zinc-800">
                  <span className="text-zinc-400 block text-[10px]">Động năng trước va chạm:</span>
                  <span className="font-bold text-zinc-100">
                    {(impactResult.energyBeforeJoules / 1000000).toFixed(2)} MJ
                  </span>
                </div>

                <div className="p-2.5 rounded bg-zinc-900 border border-zinc-800">
                  <span className="text-zinc-400 block text-[10px]">Động năng dư sau va chạm:</span>
                  <span className="font-bold text-zinc-100">
                    {(impactResult.energyAfterJoules / 1000000).toFixed(2)} MJ
                  </span>
                </div>

                <div className="p-2.5 rounded bg-zinc-900 border border-zinc-800">
                  <span className="text-zinc-400 block text-[10px]">Sát thương giáp (HP):</span>
                  <span className="font-bold text-rose-400">{impactResult.gameplayDamage} HP</span>
                </div>

                <div className="p-2.5 rounded bg-zinc-900 border border-zinc-800">
                  <span className="text-zinc-400 block text-[10px]">Sát thương mảnh vỡ (Spall):</span>
                  <span className="font-bold text-amber-400">{impactResult.spallDamage || 0} HP</span>
                </div>
              </div>

              {/* Multi-layer Breakdown */}
              {impactResult.layersTraversed && impactResult.layersTraversed.length > 0 && (
                <div className="p-3 rounded bg-zinc-900/60 border border-zinc-800 space-y-1.5 text-[10px]">
                  <span className="font-bold text-zinc-400 uppercase">Hành trình xuyên qua các lớp:</span>
                  {impactResult.layersTraversed.map((l) => (
                    <div key={l.layerIndex} className="flex justify-between text-zinc-300">
                      <span>Lớp {l.layerIndex + 1} ({l.materialId}): {l.pathLengthMm}mm LOS</span>
                      <span className={l.penetrated ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>
                        {l.penetrated ? 'Xuyên thủng' : 'Chặn đứng'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer Diagnostic Info */}
          <div className="text-[10px] text-zinc-500 pt-3 border-t border-zinc-800 flex justify-between">
            <span>Mô hình: @tankworld/armor-physics</span>
            <span>Độ tin cậy: {Math.round(impactResult.modelConfidence * 100)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};
